import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type JSX } from "react";

import { cn } from "@/lib/cn";

/**
 * Six variants (the design's five and its dark one), three sizes, six states (brief 08, section 3).
 *
 * - `primary` is the one orange action on a screen or in a dialog. `tone="ink"` turns it
 *   neutral: the topbar's "+ Yaratish" is the global create action and stays black, so the
 *   page keeps its own orange.
 * - `secondary` and `destructive` share a shape: white, a 1px line, no shadow. The
 *   destructive label is red. `tone="solid"` is the confirmation dialog's red surface.
 * - States: hover, active, focus (the global :focus-visible ring), disabled (40%) and
 *   loading (the spinner sits over the label, which keeps its width).
 */
export const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap font-medium transition-colors duration-fast ease-token disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "",
        secondary: "border border-line bg-surface text-ink hover:bg-surface-muted active:bg-sidebar-hover",
        ghost: "text-ink-muted hover:bg-surface-muted hover:text-ink active:bg-sidebar-hover",
        destructive: "",
        dark: "bg-btn-dark text-btn-dark-ink hover:bg-btn-dark-hover active:bg-btn-dark-active",
        link: "h-auto px-0 text-accent-ink underline-offset-4 hover:underline",
      },
      tone: {
        accent: "",
        ink: "",
        solid: "",
      },
      size: {
        sm: "h-7 px-2.5 text-sm",
        md: "h-9 px-3.5 text-sm",
        lg: "h-11 px-4.5 text-lg",
      },
    },
    compoundVariants: [
      { variant: "primary", tone: "accent", class: "bg-btn-primary text-btn-primary-ink hover:bg-btn-primary-hover active:bg-btn-primary-active" },
      { variant: "primary", tone: "ink", class: "bg-btn-ink text-btn-ink-ink hover:bg-btn-ink-hover active:bg-btn-ink-active" },
      { variant: "destructive", tone: "accent", class: "border border-line bg-surface text-danger-ink hover:bg-danger-soft active:bg-danger-soft" },
      { variant: "destructive", tone: "solid", class: "border border-danger bg-danger text-danger-solid-ink hover:bg-danger-hover active:bg-danger-hover" },
      { variant: "link", size: ["sm", "md", "lg"], class: "h-auto px-0" },
      { size: "sm", class: "rounded-small" },
      { size: ["md", "lg"], class: "rounded-control" },
      { variant: "link", class: "rounded-none" },
    ],
    defaultVariants: { variant: "primary", tone: "accent", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Replaces the label with a spinner of the same width and blocks a second submit. */
  loading?: boolean | undefined;
  /** Render the styles on the child element (for example a router link). */
  asChild?: boolean | undefined;
}

/**
 * A ref is forwarded because Radix triggers (dropdown, tooltip, popover) attach one
 * through `asChild`; a plain function component cannot receive it.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, tone, loading = false, asChild = false, className, children, disabled, type = "button", ...rest },
  ref,
): JSX.Element {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      className={cn(buttonVariants({ variant, size, tone }), className)}
      disabled={asChild ? undefined : disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <LoaderCircle className="animate-spin" strokeWidth={1.5} aria-hidden="true" />
            </span>
          ) : null}
          <span className={cn("inline-flex items-center gap-1.5", loading && "invisible")}>{children}</span>
        </>
      )}
    </Comp>
  );
});
